import React, { useEffect, useState } from 'react';
import { apiClient } from '@theraply/api-client';

interface DocumentExpiration {
  documentId: string;
  therapistId: number;
  therapistEmail: string;
  therapistName: string;
  documentType: string;
  expirationDate: string;
  daysUntilExpiration: number;
  urgencyLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  therapistStatus: string;
}

interface ExpiredDocument extends DocumentExpiration {
  daysExpired: number;
}


export default function ExpirationsDashboard() {
  const [upcomingExpirations, setUpcomingExpirations] = useState<DocumentExpiration[]>([]);
  const [expiredDocuments, setExpiredDocuments] = useState<ExpiredDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [upcomingRes, expiredRes] = await Promise.all([
        apiClient.get('/admin/expirations/upcoming'),
        apiClient.get('/admin/expirations/expired'),
      ]);

      setUpcomingExpirations(upcomingRes.data.expirations || []);
      setExpiredDocuments(expiredRes.data.expiredDocuments || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load expiration data');
      console.error('Failed to load expirations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (therapistId: number) => {
    try {
      setReactivating(therapistId);
      await apiClient.post(`/admin/expirations/therapists/${therapistId}/reactivate`);

      // Reload data after successful reactivation
      await loadData();

      // Show success message
      alert('Therapist reactivated successfully!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to reactivate therapist');
      console.error('Failed to reactivate:', err);
    } finally {
      setReactivating(null);
    }
  };

  const getUrgencyColor = (level: string) => {
    switch (level) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'LOW':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatDocumentType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading expiration data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadData}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">License & Insurance Expirations</h1>
        <p className="mt-2 text-gray-600">
          Monitor upcoming and expired documents to ensure compliance
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Critical (≤7 days)</div>
          <div className="mt-2 text-3xl font-bold text-red-600">
            {upcomingExpirations.filter(e => e.urgencyLevel === 'CRITICAL').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">High (8-30 days)</div>
          <div className="mt-2 text-3xl font-bold text-orange-600">
            {upcomingExpirations.filter(e => e.urgencyLevel === 'HIGH').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Medium (31-60 days)</div>
          <div className="mt-2 text-3xl font-bold text-yellow-600">
            {upcomingExpirations.filter(e => e.urgencyLevel === 'MEDIUM').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Expired</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {expiredDocuments.length}
          </div>
        </div>
      </div>

      {/* Upcoming Expirations */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Upcoming Expirations</h2>
          <p className="text-sm text-gray-500 mt-1">
            Documents expiring in the next 90 days
          </p>
        </div>
        <div className="overflow-x-auto">
          {upcomingExpirations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No upcoming expirations found
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Urgency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Therapist
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiration Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days Until
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {upcomingExpirations.map((exp) => (
                  <tr key={exp.documentId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUrgencyColor(
                          exp.urgencyLevel
                        )}`}
                      >
                        {exp.urgencyLevel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{exp.therapistName}</div>
                      <div className="text-sm text-gray-500">{exp.therapistEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDocumentType(exp.documentType)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {exp.expirationDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        {exp.daysUntilExpiration} days
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {exp.therapistStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Expired Documents / Suspended Therapists */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Suspended Therapists</h2>
          <p className="text-sm text-gray-500 mt-1">
            Therapists with expired documents requiring action
          </p>
        </div>
        <div className="overflow-x-auto">
          {expiredDocuments.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No suspended therapists
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Therapist
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expired Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days Expired
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expiredDocuments.map((exp) => (
                  <tr key={exp.documentId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{exp.therapistName}</div>
                      <div className="text-sm text-gray-500">{exp.therapistEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDocumentType(exp.documentType)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {exp.expirationDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-red-600">
                        {exp.daysExpired} days ago
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleReactivate(exp.therapistId)}
                        disabled={reactivating === exp.therapistId}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {reactivating === exp.therapistId ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Reactivating...
                          </>
                        ) : (
                          'Reactivate'
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
